(function () {
    'use strict';

    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    // --- State ---
    let mouseX = 0, mouseY = 0;
    let currentX = 0, currentY = 0;
    let isActive = false;
    /** @type {number | null} */
    let rafId = null;

    let currentScale = 0, targetScale = 0;
    let currentIconScale = 0, targetIconScale = 0;
    let currentArrowProgress = 0, targetArrowProgress = 0;
    let isOverInteractive = false;
    // Arrow offset — lerped independently so mid-transition reversals are seamless
    let currentOffsetX = 0, currentOffsetY = 0;
    let targetOffsetX = 0, targetOffsetY = 0;

    /** @type {HTMLElement | null} */
    let safeCursor = null;
    /** @type {HTMLElement | null} */
    let iconInner = null;
    /** @type {HTMLElement | null} */
    let arrowEl = null;
    /** @type {AbortController | null} */
    let containerAC = null;

    // --- Constants ---
    const LERP_SPEED = 0.18; // position tracking
    const SCALE_SPEED = 0.08; // enter / leave
    const DOWN_SCALE_SPEED = 0.28; // mousedown — snappy
    const UP_SCALE_SPEED = 0.07; // mouseup — slow restore
    const ICON_SCALE_SPEED = 0.12; // icon lags behind cursor for depth
    const ARROW_IN_SPEED = 0.15; // arrow enter — snappy response
    const ARROW_OUT_SPEED = 0.08; // arrow leave — gentle fade-off
    const ARROW_TRAVEL = 32;     // px diagonal slide distance
    const BLUR_MAX = 2;          // px — peak blur during icon crossfade
    const HALF_SIZE = 35;        // half of 70px CSS size — never changes

    const INTERACTIVE =
        'a, button, [role="button"], [role="link"], summary, input[type="submit"], label[for]';

    let activeScaleSpeed = SCALE_SPEED;
    let activeArrowSpeed = ARROW_IN_SPEED;

    // --- Helpers ---
    /**
     * Exponential lerp — moves `current` toward `target` by factor `t`.
     * @param {number} current
     * @param {number} target
     * @param {number} t
     * @returns {number}
     */
    function lerp(current, target, t) {
        return current + (target - current) * t;
    }

    // --- Track Mouse (global, once) ---
    // Use capture phase on `document` so the handler fires even when a
    // slideshow component has called setPointerCapture (which redirects
    // events) and stopImmediatePropagation (which blocks bubble-phase
    // listeners).  Capture phase runs top-down before target/bubble.
    document.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse') return;
        mouseX = e.clientX;
        mouseY = e.clientY;

        // Detect interactive elements under cursor (cheap closest() walk)
        if (isActive) {
            var over = !!/** @type {Element} */(e.target).closest(INTERACTIVE);
            if (over !== isOverInteractive) {
                isOverInteractive = over;
                targetArrowProgress = over ? 1 : 0;
                activeArrowSpeed = over ? ARROW_IN_SPEED : ARROW_OUT_SPEED;
                if (over) {
                    // If nearly invisible, snap offset to enter-from (bottom-left) so it
                    // always arrives from the right direction when re-appearing fresh
                    if (currentArrowProgress < 0.1) {
                        currentOffsetX = -ARROW_TRAVEL;
                        currentOffsetY = ARROW_TRAVEL;
                    }
                    targetOffsetX = 0;
                    targetOffsetY = 0;
                } else {
                    // Exit toward top-right
                    targetOffsetX = ARROW_TRAVEL;
                    targetOffsetY = -ARROW_TRAVEL;
                }
            }
        }
    }, true);

    // --- Click Feedback (global, once) ---
    document.addEventListener('pointerdown', function () {
        if (!isActive) return;
        activeScaleSpeed = DOWN_SCALE_SPEED;
        targetScale = 0.25;
    });

    document.addEventListener('pointerup', function () {
        if (!isActive) return;
        activeScaleSpeed = UP_SCALE_SPEED;
        targetScale = 1;
    });

    // --- Loop ---
    function startLoop() {
        if (!rafId) rafId = requestAnimationFrame(tick);
    }

    function tick() {
        if (!safeCursor) { rafId = null; return; }

        currentX = lerp(currentX, mouseX, LERP_SPEED);
        currentY = lerp(currentY, mouseY, LERP_SPEED);
        currentScale = lerp(currentScale, targetScale, activeScaleSpeed);

        safeCursor.style.transform =
            'translate3d(' +
            (currentX - HALF_SIZE) + 'px, ' +
            (currentY - HALF_SIZE) + 'px, 0' +
            ') scale(' + currentScale + ')';

        // --- Arrow (link-hover indicator) ---
        if (arrowEl) {
            currentArrowProgress = lerp(currentArrowProgress, targetArrowProgress, activeArrowSpeed);
            currentOffsetX = lerp(currentOffsetX, targetOffsetX, activeArrowSpeed);
            currentOffsetY = lerp(currentOffsetY, targetOffsetY, activeArrowSpeed);
            // Skip DOM writes while the arrow is invisible and not animating in —
            // avoids redundant filter/opacity/transform style writes at 60fps
            if (currentArrowProgress > 0.001 || targetArrowProgress > 0) {
                arrowEl.style.transform =
                    'translate(' + currentOffsetX + 'px, ' + currentOffsetY + 'px) rotate(-45deg)';
                arrowEl.style.opacity = String(currentArrowProgress);
                // Clear filter at full presence so the GPU blur pass is released
                arrowEl.style.filter = currentArrowProgress > 0.999
                    ? 'none'
                    : 'blur(' + (BLUR_MAX * (1 - currentArrowProgress)) + 'px)';
            }
        }

        // --- Existing icon (cross-fade with arrow) ---
        if (iconInner && currentScale > 0) {
            currentIconScale = lerp(currentIconScale, targetIconScale, ICON_SCALE_SPEED);
            // Scale compensation × shrink toward 0.75 as arrow appears
            iconInner.style.scale =
                String((currentIconScale / currentScale) * (1 - 0.25 * currentArrowProgress));
            // Skip opacity and filter writes when not transitioning
            if (currentArrowProgress > 0.001) {
                iconInner.style.opacity = String(1 - currentArrowProgress);
                iconInner.style.filter = 'blur(' + (BLUR_MAX * currentArrowProgress) + 'px)';
            }
        }

        const scaleSettled = Math.abs(targetScale - currentScale) < 0.001;
        const iconSettled = !iconInner || Math.abs(targetIconScale - currentIconScale) < 0.001;
        const arrowSettled = !arrowEl || (
            Math.abs(targetArrowProgress - currentArrowProgress) < 0.001 &&
            Math.abs(targetOffsetX - currentOffsetX) < 0.1 &&
            Math.abs(targetOffsetY - currentOffsetY) < 0.1
        );

        if (!isActive && scaleSettled && iconSettled && arrowSettled) {
            rafId = null;
            return;
        }

        rafId = requestAnimationFrame(tick);
    }

    // --- Teardown ---
    function destroy() {
        containerAC?.abort();
        containerAC = null;

        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }

        // Reset visual state so a stale cursor isn't left on screen
        if (safeCursor) safeCursor.classList.remove('is-active');

        isActive = false;
        targetScale = 0;
        currentScale = 0;
        targetIconScale = 0;
        currentIconScale = 0;
        currentArrowProgress = 0;
        targetArrowProgress = 0;
        isOverInteractive = false;
        currentOffsetX = 0; currentOffsetY = 0;
        targetOffsetX = 0; targetOffsetY = 0;
        safeCursor = null;
        iconInner = null;
        arrowEl = null;
    }

    // --- Init / Re-init ---
    function init() {
        destroy();

        const cursorEl = document.querySelector('[data-cursor-follower]');
        /** @type {NodeListOf<HTMLElement>} */
        const containers = document.querySelectorAll('[data-cursor-container]');

        if (!cursorEl || !containers.length) return;

        safeCursor = /** @type {HTMLElement} */ (cursorEl);
        iconInner = safeCursor.querySelector('.cursor-follower__inner');
        arrowEl = safeCursor.querySelector('.cursor-follower__arrow');
        // Prime offset to enter-from position so first appearance always slides in correctly
        currentOffsetX = -ARROW_TRAVEL; currentOffsetY = ARROW_TRAVEL;
        targetOffsetX = -ARROW_TRAVEL; targetOffsetY = ARROW_TRAVEL;
        containerAC = new AbortController();
        const signal = containerAC.signal;

        containers.forEach(function (container) {
            container.addEventListener('mouseenter', function () {
                activeScaleSpeed = SCALE_SPEED;
                currentX = mouseX;
                currentY = mouseY;
                targetScale = 1;
                targetIconScale = 1;
                safeCursor?.classList.add('is-active');
                isActive = true;
                startLoop();
            }, { signal });

            container.addEventListener('mouseleave', function () {
                activeScaleSpeed = SCALE_SPEED;
                targetScale = 0;
                targetIconScale = 0;
                // Reset arrow state — exit toward top-right then snap ready for next entry
                targetArrowProgress = 0;
                activeArrowSpeed = ARROW_OUT_SPEED;
                targetOffsetX = ARROW_TRAVEL;
                targetOffsetY = -ARROW_TRAVEL;
                isOverInteractive = false;
                safeCursor?.classList.remove('is-active');
                isActive = false;
                // Loop self-terminates in tick() once settled
            }, { signal });
        });
    }

    // --- Bootstrap ---
    init();

    // Tear down before morph so stale references are cleared,
    // then re-init after morph with fresh DOM nodes.
    document.addEventListener('main:before-morph', destroy);
    document.addEventListener('main:morphed', init);

    document.addEventListener('shopify:section:load', init);

}());
